import { useState } from "react";
import { useT, type Lang } from "@/lib/i18n";
import { postForm } from "@/lib/api";
import { Reveal } from "./Reveal";
import { SectionHeading } from "./SectionHeading";
import { SECTION_CLS } from "./Releases";
import { PendingForm, type FormField } from "./PendingForm";

const TABS = ["artist", "press", "general"] as const;
type Tab = (typeof TABS)[number];

const Newsletter = ({ lang }: { lang: Lang }) => {
  const t = useT();
  const [state, setState] = useState<"idle" | "sending" | "done">("idle");
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const email = String(fd.get("email") ?? "");
    const website = String(fd.get("website") ?? "");
    setState("sending");
    setError(null);
    const res = await postForm("/newsletter", { email, locale: lang, website });
    if (res.ok) {
      setState("done");
    } else {
      setState("idle");
      setError(
        res.detail === "invalid_email"
          ? t.newsletter.invalid
          : t.newsletter.error,
      );
    }
  };

  return (
    <div
      data-testid="newsletter-block"
      className="mt-24 border border-if-line bg-if-surface/40 p-10 md:p-14"
    >
      <h3 className="font-serif text-2xl font-light tracking-tight text-if-text">
        {t.newsletter.title}
      </h3>
      {state === "done" ? (
        <p
          data-testid="newsletter-success"
          className="mt-8 flex items-center gap-3 text-sm font-light text-if-gold"
        >
          <span className="inline-block h-1.5 w-1.5 rotate-45 bg-if-gold" />
          {t.newsletter.success}
        </p>
      ) : (
        <>
          <form onSubmit={onSubmit} className="mt-8 flex max-w-md gap-4">
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="hidden"
            />
            <input
              name="email"
              type="email"
              required
              placeholder={t.newsletter.placeholder}
              data-testid="newsletter-email-input"
              className="w-full border-b border-if-line bg-transparent py-3 text-sm text-if-text placeholder:text-if-dim/50 transition-colors duration-300 focus:border-if-gold focus:outline-none"
            />
            <button
              type="submit"
              disabled={state === "sending"}
              data-testid="newsletter-submit"
              className="shrink-0 border border-if-gold/50 px-6 py-3 text-[10px] uppercase tracking-[0.25em] text-if-gold transition-colors duration-300 hover:bg-if-gold hover:text-if-black disabled:opacity-60"
            >
              {state === "sending" ? t.newsletter.sending : t.newsletter.submit}
            </button>
          </form>
          <p className="mt-4 max-w-md text-[11px] font-light leading-relaxed text-if-dim/70">
            {t.newsletter.consent}
          </p>
          {error && (
            <p
              data-testid="newsletter-error"
              className="mt-4 text-xs font-light text-red-300/90"
            >
              {error}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export const Contact = ({
  initialTab,
  lang,
}: {
  initialTab?: Tab;
  lang: Lang;
}) => {
  const t = useT();
  const [tab, setTab] = useState<Tab>(initialTab ?? "artist");

  const forms: Record<Tab, { endpoint: string; fields: FormField[] }> = {
    artist: {
      endpoint: "/forms/artist-application",
      fields: [
        { name: "name", label: t.form.name },
        { name: "email", label: t.form.email, type: "email" },
        {
          name: "role",
          label: t.form.role,
          type: "select",
          options: [t.form.roleArtist, t.form.roleVocalist],
        },
        { name: "links", label: t.form.links, type: "url" },
        { name: "message", label: t.form.message, type: "textarea" },
      ],
    },
    press: {
      endpoint: "/forms/press",
      fields: [
        { name: "name", label: t.form.name },
        { name: "organization", label: t.form.organization },
        { name: "email", label: t.form.email, type: "email" },
        { name: "message", label: t.form.message, type: "textarea" },
      ],
    },
    general: {
      endpoint: "/forms/contact",
      fields: [
        { name: "name", label: t.form.name },
        { name: "email", label: t.form.email, type: "email" },
        { name: "subject", label: t.form.subject },
        { name: "message", label: t.form.message, type: "textarea" },
      ],
    },
  };

  return (
    <section id="contact" data-testid="contact-section" className={SECTION_CLS}>
      <SectionHeading
        index={t.contact.index}
        eyebrow={t.contact.eyebrow}
        title={t.contact.title}
        testId="contact-heading"
      />
      <div
        className="mb-12 flex flex-wrap gap-8 border-b border-if-line"
        role="tablist"
      >
        {TABS.map((k) => (
          <button
            key={k}
            role="tab"
            aria-selected={tab === k}
            data-testid={`contact-tab-${k}`}
            onClick={() => setTab(k)}
            className={`-mb-px border-b pb-4 text-[11px] uppercase tracking-[0.25em] transition-colors duration-300 ${
              tab === k
                ? "border-if-gold text-if-gold"
                : "border-transparent text-if-dim hover:text-if-text"
            }`}
          >
            {t.contact.tabs[k]}
          </button>
        ))}
      </div>
      <Reveal key={tab}>
        <div className="max-w-2xl">
          <PendingForm
            testId={`${tab}-form`}
            endpoint={forms[tab].endpoint}
            fields={forms[tab].fields}
            submitLabel={t.form.submit}
            active
          />
        </div>
      </Reveal>
      <Newsletter lang={lang} />
    </section>
  );
};
